import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FaTimesCircle,
  FaRedo,
  FaHome,
  FaHeadset,
  FaExclamationTriangle,
  FaPhone,
} from "react-icons/fa";

import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import {
  Container,
  SuccessHeader,
  SuccessIcon,
  SuccessTitle,
  SuccessMessage,
  OrderDetails,
  OrderSection,
  SectionTitle,
  OrderInfo,
  InfoRow,
  InfoLabel,
  InfoValue,
  ActionButtons,
  PrimaryButton,
  SecondaryButton,
  NextSteps,
  StepCard,
  StepIcon,
  StepContent,
} from "../OrderSuccessPage/OrderSuccessPage.styles";
import styled from "styled-components";

// Error messages for different transaction codes
const errorMessages = {
  101: "Pogrešan datum isteka kartice. Proverite podatke i pokušajte ponovo.",
  105: "Transakcija nije odobrena od strane banke. Kontaktirajte vašu banku.",
  108: "Kartica je prijavljena kao izgubljena ili ukradena.",
  111: "Kartica ne postoji ili je broj kartice pogrešan.",
  116: "Nedovoljno sredstava na računu. Proverite stanje i pokušajte ponovo.",
  130: "Prekoračen dnevni limit potrošnje na kartici.",
  290: "Banka izdavalac kartice trenutno nije dostupna. Pokušajte kasnije.",
  291: "Tehnički problem u komunikaciji. Molimo pokušajte ponovo.",
  401: "Greška u formatu podataka.",
  402: "Greška u parametrima trgovca.",
  403: "Greška u komunikaciji sa serverom.",
  404: "Greška u autentifikaciji.",
  405: "Neispravan digitalni potpis.",
  501: "Transakcija je otkazana.",
  502: "Sesija je istekla. Molimo pokušajte ponovo.",
};

const PaymentFailed = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderNumber = searchParams.get("orderNumber");
  const code = searchParams.get("code");
  const message = searchParams.get("message");

  // Get error message
  const errorMessage = message
    ? decodeURIComponent(message)
    : errorMessages[code] ||
      "Došlo je do greške prilikom plaćanja. Molimo pokušajte ponovo.";

  const handleRetry = () => {
    navigate("/korpa");
  };

  const navigateWithScroll = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer>
      <SEO title="Neuspešna porudžbina" path="/neuspesna-porudzbina" noindex />

      <Container>
        <SuccessHeader>
          <FailureIcon>
            <FaTimesCircle />
          </FailureIcon>
          <SuccessTitle>Plaćanje Nije Uspelo</SuccessTitle>
          <SuccessMessage>{errorMessage}</SuccessMessage>
        </SuccessHeader>

        <OrderDetails>
          {orderNumber && (
            <OrderSection>
              <SectionTitle>
                <FaExclamationTriangle />
                Informacije o Grešci
              </SectionTitle>
              <OrderInfo>
                <InfoRow>
                  <InfoLabel>Broj Porudžbine:</InfoLabel>
                  <InfoValue>#{orderNumber}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Vreme Pokušaja:</InfoLabel>
                  <InfoValue>
                    {new Date().toLocaleDateString("sr-Latn-RS", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </InfoValue>
                </InfoRow>
                {code && (
                  <InfoRow>
                    <InfoLabel>Kod Greške:</InfoLabel>
                    <InfoValue>{code}</InfoValue>
                  </InfoRow>
                )}
              </OrderInfo>
            </OrderSection>
          )}

          <OrderSection>
            <SectionTitle>
              <FaExclamationTriangle />
              Šta Možete Uraditi?
            </SectionTitle>
            <HelpBoxContent>
              <div className="help-item">
                <div className="help-icon">
                  <FaTimesCircle />
                </div>
                <div className="help-text">
                  <strong>Proverite podatke kartice</strong>
                  <p>
                    Uverite se da su broj kartice, datum isteka i CVV kod
                    ispravni
                  </p>
                </div>
              </div>
              <div className="help-item">
                <div className="help-icon">
                  <FaExclamationTriangle />
                </div>
                <div className="help-text">
                  <strong>Proverite stanje računa</strong>
                  <p>Uverite se da imate dovoljno sredstava na kartici</p>
                </div>
              </div>
              <div className="help-item">
                <div className="help-icon">
                  <FaRedo />
                </div>
                <div className="help-text">
                  <strong>Pokušajte sa drugom karticom</strong>
                  <p>Ako prva kartica ne radi, probajte sa drugom</p>
                </div>
              </div>
              <div className="help-item">
                <div className="help-icon">
                  <FaPhone />
                </div>
                <div className="help-text">
                  <strong>Kontaktirajte banku</strong>
                  <p>Ako problem potraje, kontaktirajte vašu banku</p>
                </div>
              </div>
            </HelpBoxContent>
          </OrderSection>
        </OrderDetails>

        <NextSteps>
          <SectionTitle>Sledeći Koraci</SectionTitle>
          <div className="steps-grid">
            <StepCard>
              <StepIcon>
                <FaRedo />
              </StepIcon>
              <StepContent>
                <h4>1. Pokušajte Ponovo</h4>
                <p>
                  Proverite podatke i pokušajte ponovo da izvršite plaćanje.
                </p>
              </StepContent>
            </StepCard>

            <StepCard>
              <StepIcon>
                <FaHeadset />
              </StepIcon>
              <StepContent>
                <h4>2. Kontaktirajte Podršku</h4>
                <p>
                  Naš tim je spreman da vam pomogne oko bilo kakvih problema.
                </p>
              </StepContent>
            </StepCard>

            <StepCard>
              <StepIcon>
                <FaPhone />
              </StepIcon>
              <StepContent>
                <h4>3. Pozovite Banku</h4>
                <p>
                  Ako problem potraje, kontaktirajte vašu banku za dodatnu
                  pomoć.
                </p>
              </StepContent>
            </StepCard>
          </div>
        </NextSteps>

        <ActionButtons>
          <PrimaryButton onClick={handleRetry}>
            <FaRedo />
            Pokušaj Ponovo
          </PrimaryButton>
          <SecondaryButton onClick={() => navigateWithScroll("/")}>
            <FaHome />
            Povratak na Početnu
          </SecondaryButton>
        </ActionButtons>

        {/* <ContactInfo>
          <h3>Potrebna vam je pomoć?</h3>
          <div className="contact-subtitle">
            Kontaktirajte nas ako imate bilo kakva pitanja o plaćanju.
          </div>
          <div className="contact-grid">
            <div className="contact-item">
              <FaHeadset />
              <div>
                <strong>Email Podrška</strong>
                <p>info@mitovka.com</p>
                <p className="contact-note">Odgovorićemo u roku od 24 sata</p>
              </div>
            </div>
          </div>
        </ContactInfo> */}
      </Container>
    </MainContainer>
  );
};

// Additional styled components for error page
const FailureIcon = styled(SuccessIcon)`
  background: var(--error);
  box-shadow: 0 10px 30px rgba(220, 53, 69, 0.3);
  animation: none;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;

    svg {
      font-size: 40px;
    }
  }
`;

const HelpBoxContent = styled.div`
  display: grid;
  gap: 20px;

  .help-item {
    display: flex;
    gap: 15px;
    padding: 20px;
    background: var(--background);
    border-radius: 15px;
    border: 1px solid var(--accent-light);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }

    .help-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 45px;
      height: 45px;
      background: rgba(220, 53, 69, 0.1);
      border-radius: 50%;
      flex-shrink: 0;

      svg {
        font-size: 20px;
        color: var(--error);
      }
    }

    .help-text {
      flex: 1;

      strong {
        display: block;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 8px;
        font-size: 16px;
      }

      p {
        margin: 0;
        color: var(--text-secondary);
        font-size: 14px;
        line-height: 1.5;
      }
    }
  }

  @media (max-width: 768px) {
    .help-item {
      gap: 12px;
      padding: 15px;

      .help-icon {
        width: 40px;
        height: 40px;

        svg {
          font-size: 18px;
        }
      }
    }
  }
`;

export default PaymentFailed;
